import { Button, CircularProgress, MenuItem, TextField } from '@mui/material';
import { Gauge, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { listAgents } from '../../services/api';
import { getApiKeyUsage } from '../../services/api-key-usage.service';
import type { Agent, ApiKey, ApiKeyUsageGroupBy, ApiKeyUsageRow } from '../../types';
import { useNotification } from '../../hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { UsageStat } from './usageShared';
import {
  defaultUsageFromDate,
  defaultUsageToDate,
  API_KEY_USAGE_GROUPS,
  apiKeyUsageRowLabel,
} from './usageUtils';
import { displayName, formatDate } from './utils';

export const PersonalApiKeyUsagePanel: React.FC<{ apiKeys: ApiKey[] }> = ({ apiKeys }) => {
  const { t } = useTranslation();
  const { error } = useNotification();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [rows, setRows] = useState<ApiKeyUsageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<ApiKeyUsageGroupBy>('api_key');
  const [from, setFrom] = useState(defaultUsageFromDate);
  const [to, setTo] = useState(defaultUsageToDate);
  const [apiKeyPublicId, setApiKeyPublicId] = useState('');
  const [agentPublicId, setAgentPublicId] = useState('');
  const [capability, setCapability] = useState('');

  const totals = rows.reduce(
    (sum, row) => ({
      requests: sum.requests + row.requests,
      usageUnits: sum.usageUnits + row.usage_units,
      chargedCredits: sum.chargedCredits + row.charged_credits,
    }),
    { requests: 0, usageUnits: 0, chargedCredits: 0 }
  );

  const loadFilterResources = async () => {
    setFiltersLoading(true);
    const agentsRes = await listAgents();
    if (agentsRes.success && agentsRes.data) {
      setAgents(agentsRes.data);
    }
    setFiltersLoading(false);
  };

  const loadUsage = async () => {
    setLoading(true);

    try {
      const response = await getApiKeyUsage({
        groupBy,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
        apiKeyPublicId: apiKeyPublicId || undefined,
        agentPublicId: agentPublicId || undefined,
        capability: capability.trim() || undefined,
      });
      setRows(response.data ?? []);
    } catch (err) {
      setRows([]);
      error(err instanceof Error ? err.message : t('apiKeys.errors.loadUsageFailed'));
    }

    setLoading(false);
  };

  useEffect(() => {
    void loadFilterResources();
  }, []);

  useEffect(() => {
    void loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, from, to, apiKeyPublicId, agentPublicId]);

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              {t('apiKeys.usage.personalTitle')}
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            {t('apiKeys.usage.personalSubtitle')}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <UsageStat label={t('apiKeys.usage.requests')} value={totals.requests} />
          <UsageStat label={t('apiKeys.usage.usageUnits')} value={totals.usageUnits} />
          <UsageStat label={t('apiKeys.usage.chargedCredits')} value={totals.chargedCredits} />
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[150px_150px_180px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
        <TextField
          fullWidth
          size="small"
          type="date"
          label={t('apiKeys.usage.from')}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          size="small"
          type="date"
          label={t('apiKeys.usage.to')}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.usage.groupBy')}
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as ApiKeyUsageGroupBy)}
        >
          {API_KEY_USAGE_GROUPS.map((group) => (
            <MenuItem key={group} value={group}>
              {t(`apiKeys.usage.groups.${group}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.usage.apiKey')}
          value={apiKeyPublicId}
          onChange={(e) => setApiKeyPublicId(e.target.value)}
        >
          <MenuItem value="">{t('apiKeys.usage.allApiKeys')}</MenuItem>
          {apiKeys.map((apiKey) => (
            <MenuItem key={apiKey.publicId} value={apiKey.publicId}>
              {displayName(apiKey)} · {apiKey.publicId}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.modal.agentResource')}
          value={agentPublicId}
          onChange={(e) => setAgentPublicId(e.target.value)}
          disabled={filtersLoading}
        >
          <MenuItem value="">{t('apiKeys.usage.allAgents')}</MenuItem>
          {agents.map((agent) => (
            <MenuItem key={agent.publicId} value={agent.publicId}>
              {agent.name} · {agent.publicId}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          size="small"
          label={t('apiKeys.usage.capability')}
          placeholder="text_to_image"
          value={capability}
          onChange={(e) => setCapability(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void loadUsage();
            }
          }}
        />
        <Button
          type="button"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
          onClick={() => void loadUsage()}
          disabled={loading}
        >
          {t('apiKeys.usage.apply')}
        </Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
        <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_110px_120px_130px_150px] bg-gray-50 text-xs font-semibold text-gray-500 dark:bg-slate-900 dark:text-slate-400">
          <div className="p-2">{t('apiKeys.usage.group')}</div>
          <div className="p-2">{t('apiKeys.usage.context')}</div>
          <div className="p-2">{t('apiKeys.usage.requests')}</div>
          <div className="p-2">{t('apiKeys.usage.usageUnits')}</div>
          <div className="p-2">{t('apiKeys.usage.chargedCredits')}</div>
          <div className="p-2">{t('apiKeys.usage.latestUsed')}</div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 border-t border-gray-100 p-4 text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
            <CircularProgress size={18} />
            {t('apiKeys.usage.loading')}
          </div>
        ) : rows.length === 0 ? (
          <div className="border-t border-gray-100 p-4 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
            {t('apiKeys.usage.personalEmpty')}
          </div>
        ) : (
          rows.map((row, index) => (
            <div
              key={`${apiKeyUsageRowLabel(row, groupBy)}-${index}`}
              className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_110px_120px_130px_150px] border-t border-gray-100 text-xs dark:border-slate-700"
            >
              <div className="min-w-0 p-2">
                <div className="truncate font-semibold text-gray-800 dark:text-slate-200">
                  {apiKeyUsageRowLabel(row, groupBy)}
                </div>
                <div className="truncate text-gray-400 dark:text-slate-500">
                  {row.latest_request_id || groupBy}
                </div>
              </div>
              <div className="min-w-0 p-2 text-gray-500 dark:text-slate-400">
                <div className="truncate">{row.capability || 'capability:-'}</div>
                <div className="truncate">{row.agent_public_id || 'agent:-'}</div>
              </div>
              <div className="p-2 text-gray-700 dark:text-slate-300">
                {row.requests.toLocaleString()}
              </div>
              <div className="p-2 text-gray-700 dark:text-slate-300">
                {row.usage_units.toLocaleString()}
              </div>
              <div className="p-2 text-gray-700 dark:text-slate-300">
                {row.charged_credits.toLocaleString()}
              </div>
              <div className="p-2 text-gray-700 dark:text-slate-300">
                {row.latest_used_at ? formatDate(row.latest_used_at, 'en-US') : '—'}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};
